"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  analyseStickiness,
  STICKY_PRESETS,
  type StickyLevel,
  type StickySentence,
} from "@/lib/tool-kit/style-checkers/sticky-sentences";

/** English strings. A translated content file overrides these through `ui`. */
export const STICKY_SENTENCE_CHECKER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a paragraph, a section or a whole draft…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "It is the case that there are a number of things that are of importance to the people of the town. Researchers measured sleep duration in 212 undergraduates over two semesters and compared grades with caffeine intake. The results of the study were not what we had expected, and this led to a reconsideration of the role of the hypothesis in the design. Night-shift nurses sleep less, and they make more errors.",
  sensitivityLabel: "How strict to be",
  strict: "Strict (semi-sticky from 45%, sticky from 55%)",
  standard: "Standard (50% and 60%)",
  relaxed: "Relaxed (55% and 65%)",
  viewLabel: "Highlight",
  viewSentences: "Whole sentences",
  viewGlue: "Glue words inside flagged sentences",
  enterPrompt:
    "Paste or type some text to see how much of each sentence is glue — the, of, is, that, it and their kin.",
  results: "Results",
  words: "Words",
  glueIndex: "Glue index",
  rated: "Sentences rated",
  sticky: "Sticky sentences",
  semi: "Semi-sticky sentences",
  readingLow:
    "The glue index is at or below 40%, the level of tightly edited prose. Check the flagged sentences anyway — an average hides the odd bad one.",
  readingMid:
    "The glue index is between 40% and 50%. That is ordinary academic prose; the flagged sentences below are the best place to start trimming.",
  readingHigh:
    "The glue index is above 50%. A draft this glue-heavy is usually full of abstract nouns and stretched verbs — try the sentences listed below.",
  readingNone:
    "No sentence is long enough to rate yet. The checker rates sentences of six or more words.",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with sticky sentences highlighted",
  legendSticky: "Sticky",
  legendSemi: "Semi-sticky",
  hoverHint: "Hover over a highlight to see the sentence's glue percentage.",
  hoverTitle: "{percent} glue — {glue} of {words} words",
  listHeading: "Stickiest sentences first",
  listHint:
    "Underlined in each card is the longest unbroken run of glue words — usually where the sentence is carrying the least meaning.",
  glueSummary:
    "one:{glue} of {words} word is glue|other:{glue} of {words} words are glue",
  longestRun: "Longest glue run:",
  nothingFlagged:
    "No sentence reaches the semi-sticky line at this setting. That does not make the writing good — it only means no sentence is mostly glue.",
  limits:
    "The list of glue words is fixed and English-only. A sticky sentence is a prompt to reread, not an error: legal and definitional sentences are often glue-heavy on purpose.",
} as const;

type Strings = typeof STICKY_SENTENCE_CHECKER_STRINGS;
type Sensitivity = keyof typeof STICKY_PRESETS;
type View = "sentences" | "glue";

const TONES = { sticky: "rose", semi: "amber" } as const;
const MAX_LISTED = 12;
const PERCENT = { style: "percent", maximumFractionDigits: 0 } as const;

function isFlagged(level: StickyLevel): level is "sticky" | "semi" {
  return level === "sticky" || level === "semi";
}

function reading(index: number, rated: number, strings: Strings): string {
  if (rated === 0) {
    return strings.readingNone;
  }
  if (index <= 0.4) {
    return strings.readingLow;
  }
  return index <= 0.5 ? strings.readingMid : strings.readingHigh;
}

function FlaggedCard({
  sentence,
  strings,
  fmt,
  locale,
}: {
  sentence: StickySentence;
  strings: Strings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
  locale: string;
}) {
  const run = sentence.longestRun;
  const before = run ? sentence.text.slice(0, run.start - sentence.start) : "";
  const inside = run
    ? sentence.text.slice(run.start - sentence.start, run.end - sentence.start)
    : "";
  const after = run ? sentence.text.slice(run.end - sentence.start) : "";
  return (
    <li className={CARD}>
      <p className="text-foreground text-sm leading-relaxed">
        {run ? (
          <>
            {before}
            <span className="underline decoration-2 decoration-rose-500 underline-offset-4">
              {inside}
            </span>
            {after}
          </>
        ) : (
          sentence.text
        )}
      </p>
      <p className="mt-2 font-medium text-muted-foreground text-xs">
        {fmt(sentence.share, PERCENT)} ·{" "}
        {tPlural(strings.glueSummary, locale, sentence.words, {
          glue: fmt(sentence.glue),
          words: fmt(sentence.words),
        })}
        {run ? ` · ${strings.longestRun} “${inside}”` : null}
      </p>
    </li>
  );
}

export default function StickySentenceCheckerWidget() {
  const strings = useToolUi(STICKY_SENTENCE_CHECKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [sensitivity, setSensitivity] = useState<Sensitivity>("standard");
  const [view, setView] = useState<View>("sentences");
  const deferred = useDeferredValue(text);
  const report = useMemo(
    () => analyseStickiness(deferred, STICKY_PRESETS[sensitivity]),
    [deferred, sensitivity]
  );

  const flagged = report.sentences.filter((s) => isFlagged(s.level));
  const highlights: TextMatch[] = flagged.flatMap((sentence) => {
    const title = t(strings.hoverTitle, {
      percent: fmt(sentence.share, PERCENT),
      glue: fmt(sentence.glue),
      words: fmt(sentence.words),
    });
    if (view === "sentences") {
      return [
        {
          start: sentence.start,
          end: sentence.end,
          category: sentence.level,
          title,
        },
      ];
    }
    return sentence.glueSpans.map((span) => ({
      start: span.start,
      end: span.end,
      category: sentence.level,
      title,
    }));
  });
  const listed = [...flagged]
    .sort((a, b) => b.share - a.share)
    .slice(0, MAX_LISTED);
  const hasText = deferred.trim().length > 0;

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.privacy}
        id="ssc-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.exampleText)}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <PillGroup
        label={strings.sensitivityLabel}
        name="ssc-sensitivity"
        onChange={setSensitivity}
        options={[
          { value: "strict", label: strings.strict },
          { value: "standard", label: strings.standard },
          { value: "relaxed", label: strings.relaxed },
        ]}
        value={sensitivity}
      />

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.glueIndex}
                value={fmt(report.index, PERCENT)}
              />
              <StatCard
                emphasis
                label={strings.sticky}
                value={fmt(report.sticky)}
              />
              <StatCard label={strings.semi} value={fmt(report.semi)} />
              <StatCard label={strings.rated} value={fmt(report.rated)} />
              <StatCard label={strings.words} value={fmt(report.words)} />
            </StatGrid>
            <Notice>{reading(report.index, report.rated, strings)}</Notice>
          </div>

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <PillGroup
              label={strings.viewLabel}
              name="ssc-view"
              onChange={setView}
              options={[
                { value: "sentences", label: strings.viewSentences },
                { value: "glue", label: strings.viewGlue },
              ]}
              value={view}
            />
            <p className="flex flex-wrap gap-3 text-sm">
              <span className={HIGHLIGHT_TONES.rose}>
                {strings.legendSticky}
              </span>
              <span className={HIGHLIGHT_TONES.amber}>
                {strings.legendSemi}
              </span>
            </p>
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={TONES}
            />
            <p className={HINT}>{strings.hoverHint}</p>
          </div>

          {listed.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.listHeading}</h3>
              <p className={HINT}>{strings.listHint}</p>
              <ul className="space-y-2">
                {listed.map((sentence) => (
                  <FlaggedCard
                    fmt={fmt}
                    key={sentence.start}
                    locale={locale}
                    sentence={sentence}
                    strings={strings}
                  />
                ))}
              </ul>
            </div>
          ) : (
            <Notice>{strings.nothingFlagged}</Notice>
          )}
          <p className={HINT}>{strings.limits}</p>
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
